import { ToolNames, ArtifactTypeNames } from "VSS/Artifacts/Constants";
import { LinkingUtilities, IArtifactData } from "VSS/Artifacts/Services";
import { ILinkedArtifact } from "TFS/WorkItemTracking/ExtensionContracts";
import { ExternalLink } from "WorkItemTracking/Scripts/TFS.WorkItemTracking";
import { getRegisteredLinkName } from "WorkItemTracking/Scripts/TFS.WorkItemTracking.Linking.Utils";
import { publishErrorToTelemetry } from "VSS/Error";
import { ExternalLinkedArtifact, ExternalArtifactType, ExternalLinkedArtifactIdentifier } from "TFS/WorkItemTracking/DataProviderContracts";
import { Debug } from "VSS/Diag";
import * as Resources from "WorkItemTracking/Scripts/Resources/TFS.Resources.WorkItemTracking";

/**
 * Creates GitHub artifact url from the github context.
 * Example: vstfs:///GitHub/{PullRequest|Commit|Issue}/{{repoInternalId}/{SHA|number}}
 *
 * @param tool Artifact tool name
 * @param type Artifact type name
 * @param gitHubExtLinkContext GitHub external link context
 */
export function createGitHubArtifactUrlFromIdentifier(tool: string, type: string, gitHubExtLinkContext: ExternalLinkedArtifactIdentifier): string {
    return LinkingUtilities.encodeUri({
        tool: tool,
        type: type,
        id: [gitHubExtLinkContext.repoInternalId, gitHubExtLinkContext.numberOrSHA].join(LinkingUtilities.URI_SEPARATOR)
    });
}

/**
 * Creates GitHub artifact link with github context from an external link
 *
 * @param link External link data from work item
 */
export function createGitHubArtifactFromExternalLink(link: ExternalLink): ILinkedArtifact {
    try {
        const artifactData: IArtifactData = LinkingUtilities.decodeUri(link.linkData.FilePath);
        const externalLinkedArtifactContext: ExternalLinkedArtifact =
            link.linkData.externalLinkContext || _getExternalLinkContextFromArtifact(artifactData);

        return {
            comment: link.getComment(),
            id: artifactData.id,
            tool: artifactData.tool,
            type: artifactData.type,
            uri: artifactData.uri,
            linkType: link.getArtifactLinkType(),
            linkTypeDisplayName: getRegisteredLinkName(link.getArtifactLinkType(), link.workItem.store),
            externalLinkedArtifactContext: externalLinkedArtifactContext
        };
    } catch {
        publishErrorToTelemetry(new Error(`Invalid artifact link: ${link.linkData.FilePath} `));
        return null;
    }
}

export function createGitHubArtifactFromExternalLinkedArtifactIdentifier(identifier: ExternalLinkedArtifactIdentifier): ILinkedArtifact {
    try {
        const uri = createGitHubArtifactUrlFromIdentifier(ToolNames.GitHub, ExternalArtifactType[identifier.itemType], identifier);
        return {
            linkTypeDisplayName: _getExternalLinkedArtifactTypeName(identifier.itemType),
            externalLinkedArtifactContext: identifier as ExternalLinkedArtifact,
            uri
        } as ILinkedArtifact;
    } catch {
        publishErrorToTelemetry(new Error(`Invalid identifier: ${JSON.stringify(identifier)} `));
        return null;
    }
}

/**
 * Get github external link context from artifact link data.
 *
 * @param artifactData Artifact data
 */
function _getExternalLinkContextFromArtifact(artifactData: IArtifactData): ExternalLinkedArtifact | undefined {
    if (artifactData.tool !== ToolNames.GitHub) {
        return undefined;
    }

    const artifactId = artifactData.id;
    const artifactIdParts = artifactId.split(LinkingUtilities.URI_SEPARATOR);

    if (!artifactIdParts || artifactIdParts.length < 2) {
        return undefined;
    }

    const [repoInternalId, numberOrSHA] = artifactId.split(LinkingUtilities.URI_SEPARATOR);
    return {
        itemType: _getExternalLinkedArtifactType(artifactData.type),
        repoInternalId: repoInternalId,
        numberOrSHA: numberOrSHA
    } as ExternalLinkedArtifact;
}

function _getExternalLinkedArtifactType(type: string): ExternalArtifactType {
    switch (type) {
        case ArtifactTypeNames.Commit:
            return ExternalArtifactType.Commit;
        case ArtifactTypeNames.PullRequest:
            return ExternalArtifactType.PullRequest;
        case ArtifactTypeNames.Issue:
            return ExternalArtifactType.Issue;
    }

    Debug.fail(`Unsupported artifact type ${type}`);
}

function _getExternalLinkedArtifactTypeName(type: ExternalArtifactType): string {
    switch (type) {
        case ExternalArtifactType.Commit:
            return Resources.LinksControlGitHubCommitText;
        case ExternalArtifactType.PullRequest:
            return Resources.LinksControlGitHubPullRequestText;
        case ExternalArtifactType.Issue:
            return Resources.LinksControlGitHubIssueText;
    }

    Debug.fail(`Unsupported external artifact type ${type}`);
}

export function getArtifactTypeFabricIconName(artifactType: ExternalArtifactType): string {
    switch (artifactType) {
        case ExternalArtifactType.Commit:
            return "BranchCommit";
        case ExternalArtifactType.PullRequest:
            return "BranchPullRequest";
        case ExternalArtifactType.Issue:
            return "WorkItem";
        default:
            return "ErrorBadge";
    }
}
